import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { useToast } from "../../../../components/Toast/ToastProvider";
import { AuthContext } from "../../../../context/AuthContext";
import { isFreelance } from "../../../../utils/constants/accountType";
import BackButton from "../../../../components/Button/BackButton";
import ClientView from "./GeneralInfo/ClientView";
import GeneralInfoEditForm from "./GeneralInfo/GeneralInfoEditForm";
import PrimaryUserEditForm from "./GeneralInfo/PrimaryUserEditForm";
import SettingsEditForm from "./GeneralInfo/SettingsEditForm";
import ContactPersonsPage from "./ContactPersons/ContactPersonsPage";
import DocumentsPage from "./ClientDocuments/DocumentsPage";
import PriceListPage from "./PriceList/PriceListPage";

const tabs = [
  "General Info",
  "Contact Persons",
  "CRM",
  "Price List",
  "Taxes",
  "Documents",
];

const toSlug = (label) => label.toLowerCase().replace(/\s+/g, "-");
const SLUG_TO_TAB = Object.fromEntries(tabs.map((t) => [toSlug(t), t]));

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  // CRM is hidden from the freelance portal.
  const freelance = isFreelance(user);
  const visibleTabs = freelance ? tabs.filter((t) => t !== "CRM") : tabs;
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const activeTab = SLUG_TO_TAB[searchParams.get("tab")] ?? "General Info";
  const VALID_EDIT_SECTIONS = ["general", "primary", "settings"];
  const editParam = searchParams.get("edit");
  const editingSection =
    activeTab === "General Info" && VALID_EDIT_SECTIONS.includes(editParam)
      ? editParam
      : null;
  const setActiveTab = (tab) => {
    setSearchParams({ tab: toSlug(tab) }, { replace: true });
  };
  const setEditingSection = (section) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", toSlug(activeTab));
    if (section) {
      next.set("edit", section);
    } else {
      next.delete("edit");
    }
    setSearchParams(next, { replace: true });
  };
  const tabActionParam = searchParams.get("action");
  const isInSubView = !!editingSection || !!tabActionParam;
  const exitSubView = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    next.delete("action");
    next.delete("id");
    setSearchParams(next, { replace: true });
  };
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await api.get(`/clients/${id}`, {
          withCredentials: true,
        });
        setClient(response.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch client details");
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [id]);

  const handleSave = async (updatedData) => {
    try {
      const response = await api.put(`/clients/${id}`, updatedData, {
        withCredentials: true,
      });
      setClient(response.data.data);
      setEditingSection(null);
      toast.success("Client updated successfully");
    } catch (err) {
      console.error("Update failed", err);
      toast.error(err.response?.data?.message || "Failed to update client");
    }
  };

  const handleResendInvitation = async () => {
    if (resendingInvitation) return;
    setResendingInvitation(true);
    try {
      const response = await api.post(
        `/clients/${id}/resend-invitation`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data?.message || "Activation email has been resent.");
    } catch (err) {
      console.error("Resend invitation failed", err);
      toast.error(err.response?.data?.message || "Failed to resend invitation.");
    } finally {
      setResendingInvitation(false);
    }
  };

  const handleDelete = async () => {
    // Confirmation is handled by the "Confirm Delete" modal in ClientView.
    try {
      await api.delete(`/clients/${id}`, { withCredentials: true });
      toast.success("Client deleted successfully");
      navigate("/clients");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete client");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  if (error)
    return (
      <div className="text-center mt-10 text-red-600 font-medium">{error}</div>
    );

  return (
    <>
        <div className="border-b mb-6 flex items-end gap-4">
          <BackButton
            label={null}
            className="mb-1.5 -ml-1.5"
            onClick={isInSubView ? exitSubView : undefined}
          />
          <div className="h-5 w-px bg-gray-200 mb-2 self-center" />
          <div className="flex space-x-5 overflow-x-auto">
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                className={`pb-2 text-sm text-gray-600 border-b-2 transition whitespace-nowrap ${
                  activeTab === tab
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent hover:text-gray-900 hover:border-gray-300"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "General Info" && (
          <>
            {!editingSection ? (
              <ClientView
                client={client}
                onEditGeneral={() => setEditingSection("general")}
                onEditPrimary={() => setEditingSection("primary")}
                onEditSettings={() => setEditingSection("settings")}
                onDelete={handleDelete}
                onResendInvitation={handleResendInvitation}
                resendingInvitation={resendingInvitation}
              />
            ) : editingSection === "general" ? (
              <GeneralInfoEditForm
                client={client}
                onSave={handleSave}
                onCancel={() => setEditingSection(null)}
              />
            ) : editingSection === "primary" ? (
              <PrimaryUserEditForm
                client={client}
                onSave={handleSave}
                onCancel={() => setEditingSection(null)}
              />
            ) : editingSection === "settings" ? (
              <SettingsEditForm
                client={client}
                onSave={(updated) => {
                  console.log("Save settings info", updated);
                  setEditingSection(null);
                }}
                onCancel={() => setEditingSection(null)}
              />
            ) : null}
          </>
        )}

        {activeTab === "Contact Persons" && (
          <ContactPersonsPage clientId={id} client={client} />
        )}

        {activeTab === "Price List" && (
          <PriceListPage clientId={id} />
        )}

        {activeTab === "Documents" && (
          <DocumentsPage clientId={id} />
        )}
    </>
  );
};

export default ClientDetailPage;
